/**
 * Compliance Audit Service
 * 
 * Frontend service for fetching PII audit logs from BigQuery
 * for compliance reporting and monitoring.
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';

// Types matching backend definitions
export type FieldCategory = 'BASIC_PII' | 'MEDICAL' | 'FINANCIAL';
export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'VIEW';
export type EntityType = 'PATIENT' | 'ENCOUNTER' | 'ALLERGY' | 'MEDICATION' | 'DIAGNOSIS' | 'INSURANCE';

export interface PiiAuditLogEntry {
  id: string;
  timestamp: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  action: AuditAction;
  entityType: EntityType;
  entityId: string;
  patientId?: string;
  patientName?: string;
  fieldName: string;
  fieldCategory: FieldCategory;
  oldValue?: string;
  newValue?: string;
  ipAddress?: string;
  facilityId?: string;
  facilityName?: string;
  metadata?: Record<string, any>;
}

export interface AuditQueryFilters {
  startDate: Date;
  endDate: Date;
  userId?: string;
  patientId?: string;
  entityType?: EntityType;
  fieldCategory?: FieldCategory;
  action?: AuditAction;
  page?: number;
  limit?: number;
}

export interface AuditQueryResult {
  logs: PiiAuditLogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
  summary: {
    totalChanges: number;
    byCategory: Record<FieldCategory, number>;
    byAction: Record<AuditAction, number>;
    byEntityType: Record<EntityType, number>;
  };
}

export interface AuditUser {
  id: string;
  name: string;
  email: string;
}

// Display name mappings
export const FIELD_CATEGORY_DISPLAY: Record<FieldCategory, string> = {
  BASIC_PII: 'Basic PII',
  MEDICAL: 'Medical Records',
  FINANCIAL: 'Financial/Insurance',
};

export const ACTION_DISPLAY: Record<AuditAction, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  VIEW: 'Viewed',
};

export const ENTITY_TYPE_DISPLAY: Record<EntityType, string> = {
  PATIENT: 'Patient',
  ENCOUNTER: 'Encounter',
  ALLERGY: 'Allergy',
  MEDICATION: 'Medication',
  DIAGNOSIS: 'Diagnosis',
  INSURANCE: 'Insurance',
};

// Color mappings for UI
export const FIELD_CATEGORY_COLORS: Record<FieldCategory, string> = {
  BASIC_PII: '#2196f3', // blue
  MEDICAL: '#4caf50', // green
  FINANCIAL: '#ff9800', // orange
};

export const ACTION_COLORS: Record<AuditAction, string> = {
  CREATE: '#4caf50', // green
  UPDATE: '#2196f3', // blue
  DELETE: '#f44336', // red
  VIEW: '#9e9e9e', // gray
};

/**
 * Fetch compliance audit logs with filters
 */
export async function getComplianceAuditLogs(filters: AuditQueryFilters): Promise<AuditQueryResult> {
  const token = await getAuthToken();
  
  const params = new URLSearchParams();
  params.append('startDate', filters.startDate.toISOString());
  params.append('endDate', filters.endDate.toISOString());
  
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.patientId) params.append('patientId', filters.patientId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.fieldCategory) params.append('fieldCategory', filters.fieldCategory);
  if (filters.action) params.append('action', filters.action);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await axios.get(
    `${API_BASE_URL}/admin/compliance/audit-logs?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
}

/**
 * Get list of users for filter dropdown
 */
export async function getAuditUsers(): Promise<AuditUser[]> {
  const token = await getAuthToken();
  
  const response = await axios.get(
    `${API_BASE_URL}/admin/compliance/audit-users`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data.users || [];
}

/**
 * Export audit logs to CSV
 */
export async function exportAuditLogs(filters: Omit<AuditQueryFilters, 'page' | 'limit'>): Promise<void> {
  const token = await getAuthToken();
  
  const params = new URLSearchParams();
  params.append('startDate', filters.startDate.toISOString());
  params.append('endDate', filters.endDate.toISOString());
  
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.patientId) params.append('patientId', filters.patientId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.fieldCategory) params.append('fieldCategory', filters.fieldCategory);
  if (filters.action) params.append('action', filters.action);

  const response = await axios.get(
    `${API_BASE_URL}/admin/compliance/audit-logs/export?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    }
  );

  // Create download link
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Generate filename with date range
  const startStr = filters.startDate.toISOString().split('T')[0];
  const endStr = filters.endDate.toISOString().split('T')[0];
  link.download = `pii-audit-report-${startStr}-to-${endStr}.csv`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Format field name for display
 */
export function formatFieldName(fieldName: string): string {
  // Convert camelCase to Title Case with spaces
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Mask sensitive values for display
 */
export function maskSensitiveValue(value: string | undefined, fieldCategory: FieldCategory): string {
  if (!value) return '—';
  
  if (fieldCategory === 'FINANCIAL') {
    // For SSN-like values, show only last 4 digits
    if (value.length >= 4) {
      return `***-**-${value.slice(-4)}`;
    }
    return '****';
  }
  
  // For other PII, show first and last character
  if (value.length <= 2) return '**';
  return `${value[0]}${'*'.repeat(Math.min(value.length - 2, 6))}${value[value.length - 1]}`;
}

